import { gql } from '@apollo/client';
import _ from 'lodash';
import type {
  GetTopicAccessWarnings_TopicFragment,
  GetTopicAccessWarnings_UserFragment,
} from '../../graphql/generated/graphqlTypes';
import topicPolicyCheck, { isMember, isOwner } from '../topicPolicyCheck';

const TOPIC_FRAGMENT = gql`
  fragment getTopicAccessWarnings_topic on Topic {
    id
    inheritParent
    ownerGroups {
      id
      allUsers {
        id
      }
    }
    memberGroups {
      id
      allUsers {
        id
      }
    }
    parent {
      id
      ...topicPolicyCheck_topic
    }
    ...topicPolicyCheck_topic
  }
  ${
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
    topicPolicyCheck.fragments.topic
  }
`;

const USER_FRAGMENT = gql`
  fragment getTopicAccessWarnings_user on User {
    id
    name
  }
`;

type TopicAccessWarning<Group, Parent> = {
  parent?: Parent;
  groups?: Group[];
} & (
  | {
      groups: Group[];
      level: 'member' | 'owner';
    }
  | {
      parent: Parent;
      level: 'owner' | 'member';
    }
);

function getTopicAccessWarnings<
  Group extends GetTopicAccessWarnings_TopicFragment['ownerGroups'][0],
  ParentTopic extends GetTopicAccessWarnings_TopicFragment['parent'],
>(
  topic: Omit<
    GetTopicAccessWarnings_TopicFragment,
    'ownerGroups' | 'memberGroups'
  > & {
    memberGroups: Group[];
    ownerGroups: Group[];
    parent?: ParentTopic;
  },
  user: GetTopicAccessWarnings_UserFragment,
): TopicAccessWarning<Group, ParentTopic> | null {
  const inherited: ParentTopic | undefined = topic.inheritParent
    ? topic.parent
    : undefined;
  const matcher = { id: user.id };

  const ownerGroupsUserIn: Group[] = (topic.ownerGroups || []).filter((t) =>
    _.some(t?.allUsers || [], matcher),
  );

  if (ownerGroupsUserIn.length > 0) {
    return { level: 'owner', groups: ownerGroupsUserIn };
  }

  if (inherited && isOwner(inherited, user)) {
    return { level: 'owner', parent: inherited };
  }

  const memberGroupsUserIn = (topic.memberGroups || []).filter((t) =>
    _.some(t?.allUsers || [], matcher),
  );

  if (memberGroupsUserIn.length > 0) {
    return { level: 'member', groups: memberGroupsUserIn };
  }

  if (inherited && isMember(inherited, user)) {
    return { level: 'member', parent: inherited };
  }

  return null;
}

getTopicAccessWarnings.fragments = {
  topic: TOPIC_FRAGMENT,
  user: USER_FRAGMENT,
};

export default getTopicAccessWarnings;
